import React, { useState, useEffect } from "react";
import ManagerKPI from "../../components/ManagerKPI";
import { toast } from "react-toastify";

const AdminTaskManager = () => {
  const [manager, setManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    accountNumber: "",
    bank: "",
    passcode: "",
  });

  // Mock initial fetch (in real app, fetch from backend API)
  useEffect(() => {
    const fetchManager = async () => {
      setLoading(true);
      try {
        // Mocking API call latency
        await new Promise((res) => setTimeout(res, 800));
        
        // Mock checking local storage or backend for existing manager
        const savedManager = localStorage.getItem("mock_admin_task_manager");
        if (savedManager) {
          const parsed = JSON.parse(savedManager);
          setManager(parsed);
          setFormData(parsed);
        }
      } catch (error) {
        
        toast.error("Failed to load manager details");
      } finally {
        setLoading(false);
      }
    };
    fetchManager();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    // Basic validation
    if (!formData.fullName || !formData.phone || !formData.passcode) {
      toast.error("Please fill required fields (Name, Phone, Passcode)");
      return;
    }
    
    setLoading(true);
    try {
      // Mocking API call to save manager
      await new Promise((res) => setTimeout(res, 800));
      localStorage.setItem("mock_admin_task_manager", JSON.stringify(formData));
      setManager(formData);
      setIsEditing(false);
      toast.success("Manager details saved successfully!");
    } catch (error) {
      toast.error("Failed to save manager details");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this manager? This will reset the targets.")) return;
    setLoading(true);
    try {
      await new Promise((res) => setTimeout(res, 800));
      localStorage.removeItem("mock_admin_task_manager");
      setManager(null);
      setFormData({ fullName: "", email: "", phone: "", accountNumber: "", bank: "", passcode: "" });
      toast.success("Manager deleted and targets reset!");
    } catch (error) {
      toast.error("Failed to delete manager");
    } finally {
      setLoading(false);
    }
  };

  // Mock KPI data
  const mockKpiData = {
    active: { target: 50, achievement: 25 }, // 50%
    trans: { target: 100, achievement: 100 }, // 100%
    apis: { target: 5, achievement: 1 }, // 20%
  };

  if (loading && !manager && !isEditing) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin h-10 w-10 border-4 border-sky-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-black text-slate-800 tracking-tight">Task Manager</h1>
        <p className="text-slate-500 mt-2 text-sm">Manage the assigned Task Manager and view their Performance KPI.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Side: Manager Form/Details */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm relative overflow-hidden">
            <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center justify-between">
              <span>Manager Details</span>
              {manager && !isEditing && (
                <div className="flex gap-2">
                  <button onClick={() => setIsEditing(true)} className="text-xs bg-sky-50 text-sky-600 px-3 py-1.5 rounded-lg font-bold hover:bg-sky-100 transition-colors">
                    Edit
                  </button>
                  <button onClick={handleDelete} className="text-xs bg-rose-50 text-rose-600 px-3 py-1.5 rounded-lg font-bold hover:bg-rose-100 transition-colors">
                    Delete
                  </button>
                </div>
              )}
            </h2>

            {!manager || isEditing ? (
              <form onSubmit={handleSave} className="space-y-4 relative z-10">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Full Name *</label>
                  <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm font-medium text-slate-800 outline-none transition-all" placeholder="John Doe" required />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Phone *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm font-medium text-slate-800 outline-none transition-all" placeholder="08012345678" required />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Passcode *</label>
                  <input type="text" name="passcode" value={formData.passcode} onChange={handleInputChange} maxLength="6" pattern="\d{6}" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm font-medium text-slate-800 outline-none transition-all font-mono tracking-widest" placeholder="6-digit PIN" required />
                  <p className="text-[10px] text-slate-400 mt-1">Required for manager to login (6 digits).</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Email Address</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm font-medium text-slate-800 outline-none transition-all" placeholder="manager@example.com" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Bank</label>
                    <input type="text" name="bank" value={formData.bank} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm font-medium text-slate-800 outline-none transition-all" placeholder="E.g. Opay" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Account No</label>
                    <input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm font-medium text-slate-800 outline-none transition-all" placeholder="1234567890" />
                  </div>
                </div>
                <div className="pt-4 flex gap-3">
                  {isEditing && (
                    <button type="button" onClick={() => { setIsEditing(false); setFormData(manager); }} className="flex-1 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors">
                      Cancel
                    </button>
                  )}
                  <button type="submit" disabled={loading} className="flex-1 py-3 bg-sky-500 text-white font-bold rounded-xl hover:bg-sky-600 transition-colors shadow-lg shadow-sky-500/20 disabled:opacity-70">
                    {loading ? "Saving..." : (manager ? "Update Manager" : "Add Manager")}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Full Name</p>
                  <p className="text-slate-800 font-bold">{manager.fullName}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Phone</p>
                    <p className="text-slate-800 font-bold font-mono">{manager.phone}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Passcode</p>
                    <p className="text-slate-800 font-bold font-mono">******</p>
                  </div>
                </div>
                {(manager.email || manager.bank) && (
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
                    {manager.email && (
                      <div>
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-0.5">Email</p>
                        <p className="text-slate-800 font-medium text-sm">{manager.email}</p>
                      </div>
                    )}
                    {(manager.bank || manager.accountNumber) && (
                      <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                        <div>
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Bank</p>
                          <p className="text-slate-800 font-medium text-sm">{manager.bank || "N/A"}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Acct No</p>
                          <p className="text-slate-800 font-medium text-sm tracking-wider">{manager.accountNumber || "N/A"}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Side: KPI Table */}
        <div className="lg:col-span-7">
          {manager ? (
            <ManagerKPI kpiData={mockKpiData} />
          ) : (
            <div className="bg-slate-50 rounded-3xl border border-dashed border-slate-300 p-8 flex flex-col items-center justify-center text-center h-full min-h-[300px]">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100 mb-4">
                <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-slate-500 font-bold text-lg mb-2">No Manager Assigned</h3>
              <p className="text-slate-400 text-sm max-w-sm">Assign a Task Manager using the form to start tracking their performance KPI automatically.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTaskManager;
